import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Card, CardBody, Container } from "reactstrap";
import { Divider, FormControlLabel, Grid, Switch } from "@mui/material";
import Breadcrumb from "../../../components/Common/Breadcrumb";
import CustomButton from "../../../components/Form/CustomButton";
import LoadingDialog from "../../../components/Common/LoadingDialog";
import Notification from "../../../components/Common/Notification";
import FormEditors from "../../Forms/FormEditors";
import {
    getHomepagePolicySettings,
    updateHomepagePolicySettings,
} from "../../../store/setting/settingApi";
import "../setting.css";

const isToggleOn = (value) => Number(value) === 1;

const HomepagePolicy = ({ userPermission }) => {
    const navigate = useNavigate();
    const editorRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [editable, setEditable] = useState(false);
    const [width, setWidth] = useState(window.innerWidth);
    const [form, setForm] = useState({ enabled: 1, body_html: "" });
    const [notification, setNotification] = useState({
        open: false,
        msg: "",
        type: "success",
    });

    const loadSettings = async () => {
        setLoading(true);

        try {
            const response = await getHomepagePolicySettings();

            if (response?.code === "success" && response?.data) {
                setForm({
                    enabled: isToggleOn(response.data.enabled) ? 1 : 0,
                    body_html: response.data.body_html || "",
                });
            } else {
                setNotification({
                    open: true,
                    msg: response?.message || "Failed to load homepage policy settings.",
                    type: "error",
                });
            }
        } catch (loadError) {
            setNotification({
                open: true,
                msg: "Failed to load homepage policy settings.",
                type: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (userPermission?.length > 0) {
            userPermission.includes("edit-setting") && setEditable(true);
            !userPermission.includes("view-setting") && navigate("/not-authorized");
        }
    }, [userPermission, navigate]);

    useEffect(() => {
        loadSettings();

        const handleResize = () => setWidth(window.innerWidth);
        window.addEventListener("resize", handleResize);

        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const handleSave = async () => {
        if (!editable) return;

        setSaving(true);

        try {
            const body_html = editorRef.current?.flushAndGetData?.() ?? form.body_html;
            const payload = {
                enabled: form.enabled,
                body_html,
            };

            const response = await updateHomepagePolicySettings(payload);

            if (response?.code === "success") {
                setNotification({
                    open: true,
                    msg: response?.warning || response?.message || "Homepage policy saved.",
                    type: response?.warning ? "warning" : "success",
                });
                if (response?.data) {
                    setForm({
                        enabled: isToggleOn(response.data.enabled) ? 1 : 0,
                        body_html: response.data.body_html || "",
                    });
                }
            } else {
                setNotification({
                    open: true,
                    msg: response?.message || "Failed to save homepage policy.",
                    type: "error",
                });
            }
        } catch (saveError) {
            setNotification({
                open: true,
                msg: "Failed to save homepage policy.",
                type: "error",
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <React.Fragment>
            {width <= 425 && editable && (
                <div className="mobile-button-container">
                    <CustomButton
                        title="Save Changes"
                        press={handleSave}
                        height={40}
                        backgroundColor="#101010"
                        textColor="white"
                        width="100%"
                        disabled={saving}
                    />
                </div>
            )}
            <div className="page-content">
                <LoadingDialog open={loading || saving} />
                <Container fluid>
                    <Notification notification={notification} setNotification={setNotification} />
                    <Breadcrumb
                        title="Homepage Policy"
                        backRoute="/settings"
                        breadcrumbItems={[
                            { title: "Settings", link: "/settings" },
                            { title: "Homepage Policy", link: "#" },
                        ]}
                    >
                        {editable && width > 425 && (
                            <CustomButton
                                title="Save Changes"
                                press={handleSave}
                                height={40}
                                backgroundColor="#101010"
                                textColor="white"
                                disabled={saving}
                            />
                        )}
                    </Breadcrumb>
                    <Card className="p-3">
                        <CardBody>
                            <div className="setting-form-section-header">HOMEPAGE POLICY SECTION</div>
                            <Divider className="my-2 mb-4" />
                            <div className="setting-desc mb-4">
                                Edit the green policy card shown on the homepage (student portal and Ariba).
                                The trust bar below it stays fixed and is not editable here. When disabled,
                                the green card is hidden on the storefront. Saving starts{" "}
                                <code>generate:page</code> in the background. The editor preview uses the
                                same policy content CSS as the storefront (rebuild ppadmin after ppapi CSS changes).
                            </div>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={isToggleOn(form.enabled)}
                                                onChange={(e) =>
                                                    setForm((prev) => ({
                                                        ...prev,
                                                        enabled: e.target.checked ? 1 : 0,
                                                    }))
                                                }
                                                disabled={!editable}
                                            />
                                        }
                                        label="Show policy section on homepage"
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <FormEditors
                                        ref={editorRef}
                                        key="homepage-policy"
                                        title="Policy content"
                                        contentPreviewClass="cms-content-body homepage-policy-body"
                                        enableBlockTypography
                                        value={form.body_html}
                                        extendedHtmlSupport
                                        setValue={(html) =>
                                            setForm((prev) => ({ ...prev, body_html: html }))
                                        }
                                    />
                                </Grid>
                            </Grid>
                            {editable && (
                                <div className="d-flex justify-content-end mt-4 pt-2">
                                    <CustomButton
                                        title="Save Changes"
                                        press={handleSave}
                                        height={40}
                                        backgroundColor="#101010"
                                        textColor="white"
                                        disabled={saving}
                                    />
                                </div>
                            )}
                        </CardBody>
                    </Card>
                </Container>
            </div>
        </React.Fragment>
    );
};

function mapStateToProps({ Login }) {
    return {
        userPermission: Login.permissionArry,
    };
}

export default connect(mapStateToProps)(HomepagePolicy);
